import {
  buildGoogleMapsSearchUrl,
  extractGooglePlaceId,
  isGoogleMapsPlaceUrl,
} from '@vibesites/runtime';

/**
 * Upstream feeds sometimes pass literal strings like "false", "null", "none"
 * instead of an empty value — treat those as missing.
 */
export function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (
    lower === 'false' ||
    lower === 'true' ||
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'none' ||
    lower === 'n/a'
  ) {
    return false;
  }
  return true;
}

/**
 * True when a URL is a Google Maps link we can safely open on mobile/desktop.
 *
 * Accepts:
 * - `/maps/place/…` on google.com
 * - Maps search / directions URLs on google.com
 * - Any URL that embeds a `place_id` we can re-normalize
 *
 * Rejects short-links and profile URLs that often look correct in the CMS
 * but fail to resolve (g.page, goo.gl, maps.app.goo.gl, business.google.com, …).
 * Live HTTP checks aren't possible from the browser (CORS); format validation
 * plus an address-based fallback is the reliable theme-level approach.
 */
export function isReliableGoogleMapsUrl(value: string | null | undefined): boolean {
  if (!isUsableUrl(value)) return false;
  const url = value!.trim();

  if (extractGooglePlaceId(url)) return true;
  if (isGoogleMapsPlaceUrl(url)) return true;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (!(parsed.protocol === 'https:' || parsed.protocol === 'http:')) return false;
    if (!(host === 'google.com' || host.endsWith('.google.com'))) return false;

    const path = parsed.pathname.toLowerCase();
    if (path.includes('/maps/search') || path.includes('/maps/dir')) return true;
    // Keyless embed / classic maps query forms.
    if (path === '/maps' || path.startsWith('/maps/')) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Normalize a preferred Google URL when possible; otherwise return null so
 * callers can fall through to usePropertyAddressUrl() / address search.
 */
export function normalizePreferredMapsUrl(
  preferredUrl: string | null | undefined,
  fullAddress?: string | null,
): string | null {
  if (!isReliableGoogleMapsUrl(preferredUrl)) return null;
  const url = preferredUrl!.trim();

  const placeId = extractGooglePlaceId(url);
  if (placeId) {
    // Rebuild a mobile-safe search URL. Prefer the real address as the query
    // so Maps still lands correctly if the place_id is stale.
    return buildGoogleMapsSearchUrl(
      hasAddress(fullAddress) ? fullAddress!.trim() : 'apartment',
      placeId,
    );
  }

  return url;
}

function hasAddress(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export interface ResolveAddressHrefOptions {
  /** Slot-group / editor URL (often bound to property_google_business_url). */
  preferredUrl?: string | null;
  /** From usePropertyAddressUrl() — place → business → address → lat/lon. */
  propertyAddressUrl?: string | null;
  /** property_full_address — last-resort maps search query. */
  fullAddress?: string | null;
  /** Final href when nothing resolves (e.g. '#' for always-valid anchors). */
  emptyHref?: string;
}

/**
 * Resolve an address / directions href with validation + fallbacks:
 *   1. Preferred URL, only if it passes Google Maps reliability checks
 *   2. usePropertyAddressUrl() result
 *   3. Constructed maps search from the full address
 *   4. emptyHref (default '')
 */
export function resolveAddressHref({
  preferredUrl,
  propertyAddressUrl,
  fullAddress,
  emptyHref = '',
}: ResolveAddressHrefOptions): string {
  const normalized = normalizePreferredMapsUrl(preferredUrl, fullAddress);
  if (normalized) return normalized;

  if (isUsableUrl(propertyAddressUrl)) {
    const fallback = propertyAddressUrl!.trim();
    // Runtime builds place_id links with query=apartment; swap in the real
    // address so Maps still resolves if the place_id is stale.
    if (hasAddress(fullAddress) && /[?&]query=apartment(?:&|$)/i.test(fallback)) {
      try {
        const placeId = new URL(fallback).searchParams.get('query_place_id');
        if (placeId) return buildGoogleMapsSearchUrl(fullAddress.trim(), placeId);
      } catch {
        // fall through to the raw runtime URL
      }
    }
    return fallback;
  }

  if (hasAddress(fullAddress)) {
    return buildGoogleMapsSearchUrl(fullAddress.trim());
  }

  return emptyHref;
}
